/**
 * Agent Management Dashboard Component
 * 
 * Comprehensive interface for managing human agents with:
 * - Agent list with status indicators
 * - Create/edit/delete agent functionality
 * - Real-time status updates
 * - Performance metrics display
 * - Role and permission management
 */

import React, { useState, useEffect, useCallback } from 'react'
import { 
  Users, 
  Plus, 
  Edit, 
  Trash2, 
  UserCheck, 
  UserX,
  Circle,
  Mail,
  Phone,
  Award,
  MessageSquare,
  Clock,
  Star,
  RefreshCw,
  Search,
  Filter
} from 'lucide-react'

import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../ui/Card'
import { Button } from '../../ui/Button'
import { Input } from '../../ui/Input'
import { Select } from '../../ui/Select'
import { Badge } from '../../ui/Badge'
import { Modal } from '../../ui/Modal'
import { ConfirmDialog } from '../../ui/ConfirmDialog'

import { agentService } from '../../../services/agents'
import { getUserUsageV2, type UserUsageV2 } from '../../../services/userUsage'
import { logger } from '../../../utils/logger'
import { cn } from '../../../utils/cn'

import type { Agent, CreateAgentRequest, UpdateAgentRequest } from '../../../services/agents'

/**
 * Agent status configuration
 */
const AGENT_STATUS_CONFIG = {
  online: {
    label: 'Online',
    color: 'green',
    bgColor: 'bg-green-50',
    textColor: 'text-green-700',
    indicatorColor: 'bg-green-400'
  },
  away: {
    label: 'Away',
    color: 'yellow',
    bgColor: 'bg-yellow-50',
    textColor: 'text-yellow-700',
    indicatorColor: 'bg-yellow-400'
  },
  busy: {
    label: 'Busy',
    color: 'red',
    bgColor: 'bg-red-50',
    textColor: 'text-red-700',
    indicatorColor: 'bg-red-400'
  },
  offline: {
    label: 'Offline',
    color: 'gray',
    bgColor: 'bg-gray-50',
    textColor: 'text-gray-700',
    indicatorColor: 'bg-gray-400'
  }
} as const

/**
 * Agent role configuration
 */
const AGENT_ROLE_CONFIG = {
  agent: { label: 'Agent', color: 'blue' },
  lead: { label: 'Lead', color: 'purple' },
  supervisor: { label: 'Supervisor', color: 'orange' },
  admin: { label: 'Admin', color: 'red' }
} as const

/**
 * Props interface
 */
interface AgentManagementDashboardProps {
  className?: string
}

/**
 * Agent card component
 */
interface AgentCardProps {
  agent: Agent
  onEdit: (agent: Agent) => void
  onDelete: (agent: Agent) => void
  onStatusChange: (agent: Agent, status: string) => void
}

const AgentCard: React.FC<AgentCardProps> = ({ 
  agent, 
  onEdit, 
  onDelete, 
  onStatusChange 
}) => {
  const statusConfig = AGENT_STATUS_CONFIG[agent.status]
  const roleConfig = AGENT_ROLE_CONFIG[agent.role]
  
  const workloadPercentage = agent.max_concurrent_chats > 0 
    ? (agent.current_chat_count / agent.max_concurrent_chats) * 100 
    : 0

  return (
    <Card className={cn(
      "hover:shadow-md transition-shadow",
      !agent.is_active && "opacity-60 border-red-200 bg-red-50"
    )}>
      <CardContent className="p-6">
        <div className="flex items-start justify-between mb-4">
          <div className="flex items-center gap-3">
            <div className="relative">
              <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center">
                <Users className="w-6 h-6 text-blue-600" />
              </div>
              <div className={cn(
                "absolute -bottom-1 -right-1 w-4 h-4 rounded-full border-2 border-white",
                statusConfig.indicatorColor
              )} />
            </div>
            
            <div>
              <h3 className="font-semibold text-gray-900">{agent.name}</h3>
              <p className="text-sm text-gray-600">{agent.email}</p>
              <div className="flex items-center gap-2 mt-1">
                <Badge 
                  variant="outline"
                  className={cn(
                    statusConfig.bgColor, 
                    statusConfig.textColor,
                    'border-current'
                  )}
                >
                  {statusConfig.label}
                </Badge>
                <Badge variant="outline" className="text-xs">
                  {roleConfig.label}
                </Badge>
                {agent.team_name && (
                  <Badge variant="outline" className="text-xs text-blue-600">
                    {agent.team_name}
                  </Badge>
                )}
                {!agent.is_active && (
                  <Badge variant="outline" className="text-xs text-red-600 bg-red-100">
                    DELETED
                  </Badge>
                )}
              </div>
            </div>
          </div>
          
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => onEdit(agent)}
            >
              <Edit className="w-4 h-4" />
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => onDelete(agent)}
              className="text-red-600 hover:text-red-700"
            >
              <Trash2 className="w-4 h-4" />
            </Button>
          </div>
        </div>
        
        {/* Status message */}
        {agent.status_message && (
          <p className="text-sm text-gray-600 mb-4 italic">
            "{agent.status_message}"
          </p>
        )}
        
        {/* Workload indicator */}
        <div className="mb-4">
          <div className="flex items-center justify-between text-sm mb-2">
            <span className="text-gray-600">Current Workload</span>
            <span className="font-medium">
              {agent.current_chat_count}/{agent.max_concurrent_chats}
            </span>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-2">
            <div
              className={cn(
                "h-2 rounded-full transition-all",
                workloadPercentage > 80 ? "bg-red-500" :
                workloadPercentage > 60 ? "bg-yellow-500" : "bg-green-500"
              )}
              style={{ width: `${Math.min(workloadPercentage, 100)}%` }}
            />
          </div>
        </div>
        
        {/* Performance metrics */}
        <div className="grid grid-cols-2 gap-4 text-sm">
          <div className="flex items-center gap-2">
            <MessageSquare className="w-4 h-4 text-gray-400" />
            <div>
              <div className="font-medium">{agent.total_conversations}</div>
              <div className="text-gray-600">Conversations</div>
            </div>
          </div>
          
          <div className="flex items-center gap-2">
            <Clock className="w-4 h-4 text-gray-400" />
            <div>
              <div className="font-medium">{agent.avg_response_time_seconds.toFixed(0)}s</div>
              <div className="text-gray-600">Avg Response</div>
            </div>
          </div>
          
          <div className="flex items-center gap-2">
            <Star className="w-4 h-4 text-gray-400" />
            <div>
              <div className="font-medium">{agent.customer_satisfaction_avg.toFixed(1)}</div>
              <div className="text-gray-600">Satisfaction</div>
            </div>
          </div>
          
          <div className="flex items-center gap-2">
            <Award className="w-4 h-4 text-gray-400" />
            <div>
              <div className="font-medium">{agent.skills.length}</div>
              <div className="text-gray-600">Skills</div>
            </div>
          </div>
        </div>
        
        {/* Skills */}
        {agent.skills.length > 0 && (
          <div className="mt-4 pt-4 border-t border-gray-100">
            <div className="text-xs text-gray-600 mb-2">Skills</div>
            <div className="flex flex-wrap gap-1">
              {agent.skills.slice(0, 3).map(skill => (
                <Badge key={skill} variant="outline" className="text-xs">
                  {skill}
                </Badge>
              ))}
              {agent.skills.length > 3 && (
                <Badge variant="outline" className="text-xs text-gray-500">
                  +{agent.skills.length - 3} more
                </Badge>
              )}
            </div>
          </div>
        )}
        
        {/* Quick status change */}
        <div className="mt-4 pt-4 border-t border-gray-100">
          <div className="text-xs text-gray-600 mb-2">Quick Status Change</div>
          <div className="flex gap-1">
            {Object.entries(AGENT_STATUS_CONFIG).map(([status, config]) => (
              <Button
                key={status}
                variant="outline"
                size="sm"
                onClick={() => onStatusChange(agent, status)}
                className={cn(
                  "text-xs px-2 py-1",
                  agent.status === status && config.bgColor + ' ' + config.textColor
                )}
              >
                {config.label}
              </Button>
            ))}
          </div>
        </div>
      </CardContent>
    </Card>
  )
}

/**
 * Create/Edit Agent Modal Component
 */
interface AgentModalProps {
  isOpen: boolean
  onClose: () => void
  agent?: Agent | null
  onSave: (agentData: CreateAgentRequest | UpdateAgentRequest) => void
}

const AgentModal: React.FC<AgentModalProps> = ({ 
  isOpen, 
  onClose, 
  agent, 
  onSave 
}) => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    role: 'agent',
    max_concurrent_chats: 5,
    skills: '',
    languages: 'en',
    team_id: ''
  })
  
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [teams, setTeams] = useState<any[]>([])
  const [teamsLoading, setTeamsLoading] = useState(false)
  
  // Fetch teams for assignment
  const fetchTeams = async () => {
    try {
      setTeamsLoading(true)
      const { api } = await import('../../../services/api')
      const response = await api.get('/teams/')
      setTeams(response.data)
    } catch (error) {
      console.error('Failed to fetch teams:', error)
    } finally {
      setTeamsLoading(false)
    }
  }
  
  // Initialize form data when modal opens
  useEffect(() => {
    if (isOpen) {
      // Fetch teams when modal opens
      fetchTeams()
      
      if (agent) {
        setFormData({
          name: agent.name,
          email: agent.email,
          role: agent.role,
          max_concurrent_chats: agent.max_concurrent_chats,
          skills: agent.skills.join(', '),
          languages: agent.languages.join(', '),
          team_id: agent.team_id || '' // Use current team assignment
        })
      } else {
        setFormData({
          name: '',
          email: '',
          role: 'agent',
          max_concurrent_chats: 5,
          skills: '',
          languages: 'en',
          team_id: ''
        })
      }
      setError(null)
    }
  }, [isOpen, agent])
  
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setIsLoading(true)
    setError(null)
    
    try {
      const agentData = {
        name: formData.name,
        email: formData.email,
        role: formData.role,
        max_concurrent_chats: formData.max_concurrent_chats,
        skills: formData.skills.split(',').map(s => s.trim()).filter(s => s.length > 0),
        languages: formData.languages.split(',').map(l => l.trim()).filter(l => l.length > 0),
        team_id: formData.team_id
      }
      
      await onSave(agentData)
      onClose()
    } catch (error) {
      setError(error instanceof Error ? error.message : 'Failed to save agent')
    } finally {
      setIsLoading(false)
    }
  }
  
  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={agent ? 'Edit Agent' : 'Create New Agent'}
      size="lg"
    >
      <form onSubmit={handleSubmit} className="space-y-6">
        {error && (
          <div className="p-4 bg-red-50 border border-red-200 rounded-lg text-red-700">
            {error}
          </div>
        )}
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Full Name *
            </label>
            <Input
              type="text"
              value={formData.name}
              onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
              placeholder="Enter agent's full name"
              required
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Email Address *
            </label>
            <Input
              type="email"
              value={formData.email}
              onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
              placeholder="agent@company.com"
              required
            />
          </div>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Role
            </label>
            <Select
              value={formData.role}
              onChange={(value) => setFormData(prev => ({ ...prev, role: value }))}
              options={[
                { value: 'agent', label: 'Agent' },
                { value: 'lead', label: 'Lead' },
                { value: 'supervisor', label: 'Supervisor' },
                { value: 'admin', label: 'Admin' }
              ]}
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Max Concurrent Chats
            </label>
            <Input
              type="number"
              min="1"
              max="20"
              value={formData.max_concurrent_chats}
              onChange={(e) => setFormData(prev => ({ 
                ...prev, 
                max_concurrent_chats: parseInt(e.target.value) || 5 
              }))}
            />
          </div>
        </div>
        
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Team Assignment *
          </label>
          <Select
            value={formData.team_id}
            onChange={(value) => setFormData(prev => ({ ...prev, team_id: value }))}
            options={[
              { value: '', label: 'Select a team...' },
              ...teams.map(team => ({
                value: team.id,
                label: team.name
              }))
            ]}
            disabled={teamsLoading}
          />
          <p className="text-xs text-gray-500 mt-1">
            {teamsLoading ? 'Loading teams...' : 'Agents must be assigned to at least one team'}
          </p>
        </div>
        
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Skills (comma-separated)
          </label>
          <Input
            type="text"
            value={formData.skills}
            onChange={(e) => setFormData(prev => ({ ...prev, skills: e.target.value }))}
            placeholder="billing, technical, sales, customer service"
          />
          <p className="text-xs text-gray-500 mt-1">
            Enter skills separated by commas. These help with automatic assignment.
          </p>
        </div>
        
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Languages (comma-separated)
          </label>
          <Input
            type="text"
            value={formData.languages}
            onChange={(e) => setFormData(prev => ({ ...prev, languages: e.target.value }))}
            placeholder="en, es, fr"
          />
          <p className="text-xs text-gray-500 mt-1">
            Language codes (e.g., en for English, es for Spanish)
          </p>
        </div>
        
        <div className="flex gap-3 pt-6">
          <Button
            type="submit"
            disabled={isLoading || !formData.name || !formData.email || !formData.team_id}
            className="flex-1"
          >
            {isLoading ? (
              <>
                <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                {agent ? 'Updating...' : 'Creating...'}
              </>
            ) : (
              agent ? 'Update Agent' : 'Create Agent'
            )}
          </Button>
          <Button
            type="button"
            variant="outline"
            onClick={onClose}
            disabled={isLoading}
          >
            Cancel
          </Button>
        </div>
      </form>
    </Modal>
  )
}

/**
 * Main Agent Management Dashboard Component
 */
export const AgentManagementDashboard: React.FC<AgentManagementDashboardProps> = ({
  className
}) => {
  // ====================================
  // STATE MANAGEMENT
  // ====================================
  
  const [agents, setAgents] = useState<Agent[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  
  // Modal states
  const [isAgentModalOpen, setIsAgentModalOpen] = useState(false)
  const [editingAgent, setEditingAgent] = useState<Agent | null>(null)
  const [deletingAgent, setDeletingAgent] = useState<Agent | null>(null)
  const [usageV2, setUsageV2] = useState<UserUsageV2 | null>(null)
  
  // Filters
  const [searchQuery, setSearchQuery] = useState('')
  const [statusFilter, setStatusFilter] = useState('all')
  const [roleFilter, setRoleFilter] = useState('all')
  const [showInactive, setShowInactive] = useState(false)
  
  // ====================================
  // DATA FETCHING
  // ====================================
  
  const fetchAgents = useCallback(async () => {
    try {
      setIsLoading(true)
      setError(null)
      
      const agentsData = await agentService.getAgents(showInactive) // Include/exclude inactive based on filter
      setAgents(agentsData)
      
      logger.info('Agents loaded successfully', {
        component: 'AgentManagementDashboard',
        action: 'agents_loaded',
        count: agentsData.length
      })
      
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to load agents'
      setError(errorMessage)
      
      logger.error('Failed to fetch agents', {
        component: 'AgentManagementDashboard',
        action: 'fetch_error',
        error: errorMessage
      })
    } finally {
      setIsLoading(false)
    }
  }, [showInactive])
  
  // ====================================
  // EFFECTS
  // ====================================
  
  useEffect(() => {
    fetchAgents()
    getUserUsageV2().then(setUsageV2).catch(() => null)
  }, [fetchAgents])
  
  // ====================================
  // EVENT HANDLERS
  // ====================================
  
  const handleCreateAgent = () => {
    setEditingAgent(null)
    setIsAgentModalOpen(true)
  }
  
  const handleEditAgent = (agent: Agent) => {
    setEditingAgent(agent)
    setIsAgentModalOpen(true)
  }
  
  const handleDeleteAgent = (agent: Agent) => {
    setDeletingAgent(agent)
  }
  
  const handleConfirmDelete = async () => {
    if (!deletingAgent) return
    
    try {
      await agentService.deleteAgent(deletingAgent.id)
      await fetchAgents() // Refresh list
      setDeletingAgent(null)
      
      logger.info('Agent deleted successfully', {
        component: 'AgentManagementDashboard',
        action: 'agent_deleted',
        agentId: deletingAgent.id
      })
    } catch (error) {
      logger.error('Failed to delete agent', {
        component: 'AgentManagementDashboard',
        action: 'delete_error',
        error: error instanceof Error ? error.message : 'Unknown error'
      })
    }
  }
  
  const handleSaveAgent = async (agentData: CreateAgentRequest | UpdateAgentRequest) => {
    try {
      if (editingAgent) {
        await agentService.updateAgent(editingAgent.id, agentData as UpdateAgentRequest)
      } else {
        await agentService.createAgent(agentData as CreateAgentRequest)
      }
      
      await fetchAgents() // Refresh list
      
      logger.info('Agent saved successfully', {
        component: 'AgentManagementDashboard',
        action: editingAgent ? 'agent_updated' : 'agent_created'
      })
    } catch (error) {
      logger.error('Failed to save agent', {
        component: 'AgentManagementDashboard',
        action: 'save_error',
        error: error instanceof Error ? error.message : 'Unknown error'
      })
      throw error // Re-throw to be handled by modal
    }
  }
  
  const handleStatusChange = async (agent: Agent, status: string) => {
    try {
      await agentService.updateAgentStatus(agent.id, { 
        status: status as any 
      })
      await fetchAgents() // Refresh list
      
      logger.info('Agent status updated', {
        component: 'AgentManagementDashboard',
        action: 'status_updated',
        agentId: agent.id,
        status
      })
    } catch (error) {
      logger.error('Failed to update agent status', {
        component: 'AgentManagementDashboard',
        action: 'status_update_error',
        error: error instanceof Error ? error.message : 'Unknown error'
      })
    }
  }
  
  // ====================================
  // FILTERING
  // ====================================
  
  const filteredAgents = agents.filter(agent => {
    const matchesSearch = agent.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         agent.email.toLowerCase().includes(searchQuery.toLowerCase())
    const matchesStatus = statusFilter === 'all' || agent.status === statusFilter
    const matchesRole = roleFilter === 'all' || agent.role === roleFilter
    
    return matchesSearch && matchesStatus && matchesRole
  })
  
  // ====================================
  // RENDER HELPERS
  // ====================================
  
  const renderFilters = () => (
    <div className="flex flex-col sm:flex-row gap-4 mb-6">
      <div className="flex-1">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
          <Input
            type="text"
            placeholder="Search agents..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10"
          />
        </div>
      </div>
      
      <div className="flex gap-2">
        <Select
          value={statusFilter}
          onChange={setStatusFilter}
          options={[
            { value: 'all', label: 'All Status' },
            { value: 'online', label: 'Online' },
            { value: 'away', label: 'Away' },
            { value: 'busy', label: 'Busy' },
            { value: 'offline', label: 'Offline' }
          ]}
        />
        
        <Select
          value={roleFilter}
          onChange={setRoleFilter}
          options={[
            { value: 'all', label: 'All Roles' },
            { value: 'agent', label: 'Agent' },
            { value: 'lead', label: 'Lead' },
            { value: 'supervisor', label: 'Supervisor' },
            { value: 'admin', label: 'Admin' }
          ]}
        />
        
        <Button
          onClick={() => setShowInactive(!showInactive)}
          variant={showInactive ? "default" : "outline"}
          className="flex items-center gap-2"
        >
          {showInactive ? "Hide Deleted" : "Show Deleted"}
        </Button>
        
        <Button
          onClick={fetchAgents}
          variant="outline"
          className="flex items-center gap-2"
        >
          <RefreshCw className="w-4 h-4" />
          Refresh
        </Button>
      </div>
    </div>
  )
  
  const renderAgentGrid = () => {
    if (isLoading) {
      return (
        <div className="flex items-center justify-center py-12">
          <RefreshCw className="w-6 h-6 animate-spin text-gray-400" />
          <span className="ml-2 text-gray-600">Loading agents...</span>
        </div>
      )
    }
    
    if (error) {
      return (
        <div className="flex items-center justify-center py-12">
          <div className="text-center">
            <UserX className="w-8 h-8 text-red-500 mx-auto mb-2" />
            <p className="text-red-600">{error}</p>
            <Button
              onClick={fetchAgents}
              variant="outline"
              className="mt-4"
            >
              Try Again
            </Button>
          </div>
        </div>
      )
    }
    
    if (filteredAgents.length === 0) {
      return (
        <div className="flex items-center justify-center py-12">
          <div className="text-center">
            <Users className="w-8 h-8 text-gray-400 mx-auto mb-2" />
            <p className="text-gray-600">
              {agents.length === 0 ? 'No agents created yet' : 'No agents match your filters'}
            </p>
            {agents.length === 0 && (
              <Button
                onClick={handleCreateAgent}
                className="mt-4"
              >
                <Plus className="w-4 h-4 mr-2" />
                Create First Agent
              </Button>
            )}
          </div>
        </div>
      )
    }
    
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {filteredAgents.map(agent => (
          <AgentCard
            key={agent.id}
            agent={agent}
            onEdit={handleEditAgent}
            onDelete={handleDeleteAgent}
            onStatusChange={handleStatusChange}
          />
        ))}
      </div>
    )
  }
  
  // ====================================
  // RENDER
  // ====================================
  
  return (
    <div className={cn("space-y-6", className)}>
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Agent Management</h1>
          <p className="text-gray-600">
            Manage your human support agents and their availability
          </p>
        </div>
        
        <Button
          onClick={handleCreateAgent}
          className="flex items-center gap-2"
          disabled={!!(usageV2 && usageV2.team_members.limit !== -1 && usageV2.team_members.used >= usageV2.team_members.limit)}
          title={usageV2 && usageV2.team_members.limit !== -1 && usageV2.team_members.used >= usageV2.team_members.limit ? 'Team members limit reached. Upgrade plan to add more.' : undefined}
        >
          <Plus className="w-4 h-4" />
          Add Agent
        </Button>
      </div>
      
      {/* Summary metrics */}
      {agents.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Total Agents</p>
                  <p className="text-2xl font-bold">{agents.filter(a => a.is_active).length}</p>
                </div>
                <Users className="w-8 h-8 text-blue-500" />
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Online Now</p>
                  <p className="text-2xl font-bold text-green-600">
                    {agents.filter(a => a.is_online).length}
                  </p>
                </div>
                <UserCheck className="w-8 h-8 text-green-500" />
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Active Chats</p>
                  <p className="text-2xl font-bold text-orange-600">
                    {agents.reduce((sum, agent) => sum + agent.current_chat_count, 0)}
                  </p>
                </div>
                <MessageSquare className="w-8 h-8 text-orange-500" />
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Avg Rating</p>
                  <p className="text-2xl font-bold text-yellow-600">
                    {agents.length > 0 
                      ? (agents.reduce((sum, agent) => sum + agent.customer_satisfaction_avg, 0) / agents.length).toFixed(1)
                      : '0.0'
                    }
                  </p>
                </div>
                <Star className="w-8 h-8 text-yellow-500" />
              </div>
            </CardContent>
          </Card>
        </div>
      )}
      
      {/* Filters */}
      {renderFilters()}
      
      {/* Agent Grid */}
      {renderAgentGrid()}
      
      {/* Modals */}
      <AgentModal
        isOpen={isAgentModalOpen}
        onClose={() => setIsAgentModalOpen(false)}
        agent={editingAgent}
        onSave={handleSaveAgent}
      />
      
      <ConfirmDialog
        isOpen={!!deletingAgent}
        onClose={() => setDeletingAgent(null)}
        onConfirm={handleConfirmDelete}
        title="Delete Agent"
        description={`Are you sure you want to delete ${deletingAgent?.name}? This action cannot be undone.`}
        confirmText="Delete"
        confirmVariant="destructive"
      />
    </div>
  )
}

export default AgentManagementDashboard
